/**
 * Input Component - Enhanced form input with label, error, and helper text
 */

import React, { forwardRef } from 'react';
import { colors, spacing, borderRadius, transitions } from '../../styles/design-tokens';

const Input = forwardRef(({
  label,
  error,
  helper,
  className = '',
  icon = null,
  iconPosition = 'left',
  variant = 'flat',
  size = 'md',
  required = false,
  fullWidth = true,
  ...props
}, ref) => {
  const variants = {
    flat: 'bg-transparent border-b-2 border-neutral-200 focus:border-brand px-0 py-3',
    box: 'bg-white border-2 border-neutral-200 focus:border-brand px-4 py-3 rounded-lg',
    outline: 'bg-transparent border-2 border-neutral-200 focus:border-brand px-4 py-3 rounded-lg',
    ghost: 'bg-neutral-50 border-2 border-transparent focus:border-brand px-4 py-3 rounded-lg',
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg',
  };

  const hasError = !!error;
  const hasIcon = !!icon;

  const inputStyles = `
    w-full
    font-medium
    text-ink
    placeholder:text-neutral-400
    transition-all duration-200
    focus:outline-none
    disabled:opacity-50 disabled:cursor-not-allowed
    ${hasError ? 'border-error focus:border-error' : ''}
    ${variants[variant] || variants.flat}
    ${sizes[size] || sizes.md}
    ${hasIcon && iconPosition === 'left' ? 'pl-10' : ''}
    ${hasIcon && iconPosition === 'right' ? 'pr-10' : ''}
    ${className}
  `;

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label className="block text-xs font-semibold uppercase tracking-wider text-muted mb-1.5">
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {icon && iconPosition === 'left' && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
            {icon}
          </span>
        )}
        
        <input
          ref={ref}
          className={inputStyles}
          aria-invalid={hasError}
          aria-describedby={helper ? 'helper-text' : undefined}
          {...props}
        />
        
        {icon && iconPosition === 'right' && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
            {icon}
          </span>
        )}
      </div>
      
      {helper && !hasError && (
        <p id="helper-text" className="mt-1 text-xs text-muted">
          {helper}
        </p>
      )}
      
      {hasError && (
        <p className="mt-1 text-xs text-error">{error}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;